import type { KnownContacts } from './knownContacts'
import { type DialledPhone, insertedPhone, pastedPhone } from './phoneDraft'
import { useContactEligibility } from './useContactEligibility'
import { useEffect, useRef, useState } from 'react'
import type { TextInput as RNTextInput } from 'react-native'
import {
  Animated,
  Keyboard,
  ScrollView,
  StyleSheet,
  TextInput,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { FiatErrorNotice } from './FiatErrorNotice'
import { FiatSubmissionIndicator } from './FiatSubmissionIndicator'
import { FiatActionButton } from './FiatActionButton'
import { useKeyboardFollow } from '../flow/useKeyboardHeight'
import { useFlowStepFrame } from '../hooks/useNestedSheetHeight'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { withAlpha } from '../utils/withAlpha'
import { inputTextStyle } from '../theme/inputTextStyle'
import { Box, Text, useTheme } from '../theme/restyle'
import { logger } from '../logging/logger'
import { FiatBackButton } from './FiatBackButton'
import { FiatCountrySheet } from './FiatCountrySheet'
import { FiatPhoneField } from './FiatPhoneField'
import { PADS_WITHOUT_RETURN_KEY } from './keyboardReturnKey'
import { KYC_FORM_METRICS } from './kycFormTheme'
import {
  type PhoneCountry,
  defaultPhoneCountry,
  toE164,
} from './phoneCountries'
import type { SessionContactField } from './sessionContact'
import {
  areContactsComplete,
  isCompleteContact,
  normalizeContact,
} from './sessionContact'
import { useCountrySheet } from './useCountrySheet'

export interface FiatContactScreenProps {
  /**
   * What the session transition asks for, in the order it declares it. More
   * than one when the step verifies a number *and* an address.
   */
  fields: readonly SessionContactField[]
  /**
   * The buyer's region, ISO 3166-1 alpha-2, so a number typed the way they
   * dial it at home is read with their country code.
   */
  defaultRegion?: string
  /** Why the last send was refused, when it was; Continue sends again. */
  errorMessage?: string
  requiresContactChange?: boolean
  initialContacts?: KnownContacts
  isSubmitting: boolean
  /** Where the buyer is, per the geo lookup: the code the field opens on. */
  defaultCountry?: string
  onChangeContact: (field: SessionContactField, value: string) => void
  onSubmit: () => void
  onClose: () => void
}

/**
 * Where the code is sent (screen 2's first half).
 *
 * Collected before the transition that opens the session, so the step is the
 * client's own rather than one the server drives. Every field the step asks
 * for is on it: the session cannot open on a subset.
 */
export function FiatContactScreen(props: FiatContactScreenProps) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      props.fields.map((field) => {
        if (field.kind === 'EMAIL') {
          return [field.name, props.initialContacts?.email ?? '']
        }
        const phone = props.initialContacts?.phone
        return [
          field.name,
          phone === undefined ? '' : (pastedPhone(phone)?.national ?? ''),
        ]
      }),
    ),
  )
  return (
    <FiatContactContent
      {...props}
      values={values}
      onChangeDraft={(name, value) =>
        setValues((held) => ({ ...held, [name]: value }))
      }
    />
  )
}

interface FiatContactContentProps extends FiatContactScreenProps {
  values: Record<string, string>
  onChangeDraft: (name: string, value: string) => void
}

export function FiatContactContent({
  fields,
  defaultRegion,
  errorMessage,
  requiresContactChange = false,
  initialContacts,
  isSubmitting,
  defaultCountry,
  onChangeContact,
  onSubmit,
  onClose,
  values,
  onChangeDraft,
}: FiatContactContentProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const { height, paddingTop } = useFlowStepFrame()
  const insets = useSafeAreaInsets()
  const keyboard = useKeyboardFollow(true)
  const keyboardHeight = keyboard.height
  const [attempted, setAttempted] = useState(false)
  const [editedAfterError, setEditedAfterError] = useState(false)
  useEffect(() => setEditedAfterError(false), [errorMessage, isSubmitting])
  const shownError = editedAfterError ? undefined : errorMessage
  const [refusedContact, setRefusedContact] = useState<string>()
  useEffect(() => {
    if (isSubmitting) {
      Keyboard.dismiss()
    }
  }, [isSubmitting])
  const [countries, setCountries] = useState<Record<string, string>>(() =>
    knownPhoneCountries(fields, initialContacts?.phone),
  )
  const inputs = useRef<Record<string, RNTextInput | null>>({})
  const countrySheet = useCountrySheet()

  const countryFor = (field: SessionContactField) =>
    defaultPhoneCountry(
      countries[field.name] ?? defaultCountry ?? defaultRegion,
    )
  const contactValue = (field: SessionContactField, value: string) => {
    if (field.kind === 'PHONE') {
      return toE164(countryFor(field), value)
    }
    return normalizeContact(field, value, defaultRegion)
  }
  const submittedValues = Object.fromEntries(
    fields.map((field) => [
      field.name,
      contactValue(field, values[field.name] ?? ''),
    ]),
  )
  const submittedKey = JSON.stringify(submittedValues)
  // A round that asks for a different contact means this one: restoring it
  // after an edit is still the value the server refused.
  useEffect(() => {
    if (errorMessage !== undefined && requiresContactChange) {
      setRefusedContact(submittedKey)
    }
  }, [errorMessage, requiresContactChange, isSubmitting])
  const rejected =
    requiresContactChange &&
    (shownError !== undefined || refusedContact === submittedKey)
  const isComplete = areContactsComplete(fields, submittedValues, defaultRegion)
  const showContinue = useContactEligibility(isComplete, submittedKey)
  const edit = (field: SessionContactField, next: string) => {
    if (isSubmitting) {
      return
    }
    setAttempted(false)
    setEditedAfterError(true)
    const phone =
      field.kind === 'PHONE'
        ? insertedPhoneEdit(values[field.name] ?? '', next, countryFor(field))
        : undefined
    if (phone !== undefined) {
      setCountries((held) => ({ ...held, [field.name]: phone.country.alpha2 }))
      onChangeDraft(field.name, phone.national)
      onChangeContact(field, toE164(phone.country, phone.national))
      return
    }
    // Preserve the draft: normalizing it would erase a leading trunk digit mid-entry.
    onChangeDraft(field.name, next)
    onChangeContact(field, contactValue(field, next))
  }
  // The lookup can land after the number was typed under the fallback code.
  // The chip moves to the buyer's country and validation re-reads the digits
  // with it; the flow has to as well, or Continue sends what the earlier edit
  // reported. A code the user chose is theirs, whatever the lookup says.
  const fallback = defaultCountry ?? defaultRegion
  const lastFallback = useRef(fallback)
  useEffect(() => {
    const previous = defaultPhoneCountry(lastFallback.current)
    lastFallback.current = fallback
    for (const field of fields) {
      const draft = values[field.name]
      if (
        field.kind !== 'PHONE' ||
        draft === undefined ||
        countries[field.name] !== undefined
      ) {
        continue
      }
      if (isCompleteContact(field, draft, previous.alpha2)) {
        setCountries((known) => ({ ...known, [field.name]: previous.alpha2 }))
        continue
      }
      onChangeContact(field, contactValue(field, draft))
    }
  }, [fallback])
  const chooseCountry = (field: SessionContactField, country: PhoneCountry) => {
    setCountries((held) => ({ ...held, [field.name]: country.alpha2 }))
    onChangeContact(field, toE164(country, values[field.name] ?? ''))
    countrySheet.close()
  }
  // The last field advances the screen; the ones above it hand focus down,
  // so a two-field step is not a tap between every answer.
  const submit = () => {
    if (isSubmitting || rejected) {
      return
    }
    setAttempted(true)
    if (isComplete) {
      onSubmit()
    }
  }
  const advanceFrom = (index: number) => {
    if (isSubmitting) {
      return
    }
    const next = fields[index + 1]
    if (next === undefined) {
      submit()
      return
    }
    inputs.current[next.name]?.focus()
  }

  // What the sheet is answering, when it is answering anything: the props go
  // together, since a list with no field to hand its choice to draws nothing.
  const pickingField = fields.find(
    (field) => field.name === countrySheet.fieldName,
  )
  const picking =
    pickingField === undefined
      ? { onSelect: noop }
      : {
          selected: countryFor(pickingField),
          onSelect: (country: PhoneCountry) =>
            chooseCountry(pickingField, country),
        }

  return (
    <Animated.View
      style={{
        // A flow step's sheet spans the window, status bar included, so the
        // screen takes the whole window and pays the inset itself.
        height,
        paddingTop,
        // The keyboard shortens the box. A scroll inset would not: it lets
        // the action be scrolled to, while the action rests at the bottom of
        // a viewport the keyboard still covers.
        paddingBottom: keyboard.animated,
        backgroundColor: theme.background.modal,
      }}
    >
      <FiatBackButton onPress={onClose} />

      <ScrollView
        style={styles.scroll}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[
          styles.content,
          {
            // The keyboard already covers the home indicator.
            paddingBottom:
              keyboardHeight > 0
                ? KYC_FORM_METRICS.advanceGap
                : Math.max(0, insets.bottom - 7),
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <Box style={styles.ask}>
          <Text
            weight="medium"
            style={[
              styles.heading,
              { color: withAlpha(theme.content.elements.primary, 0.6) },
            ]}
          >
            {heading(t, fields)}
          </Text>

          <Box style={styles.rows}>
            {fields.map((field, index) => (
              <ContactField
                key={field.name}
                field={field}
                value={values[field.name] ?? ''}
                country={countryFor(field)}
                isPickingCountry={
                  countrySheet.isOpen && countrySheet.fieldName === field.name
                }
                // The first is where the step starts; the rest are reached by
                // the return key or a tap.
                autoFocus={index === 0 && !isSubmitting}
                editable={!isSubmitting}
                invalid={(attempted || shownError !== undefined) && !isComplete}
                isLast={index === fields.length - 1}
                onChangeText={(next) => edit(field, next)}
                onOpenCountries={() => countrySheet.open(field.name)}
                // Typing somewhere is choosing that instead of the list.
                onFocus={countrySheet.close}
                onAdvance={() => advanceFrom(index)}
                onLayoutRef={(input) => {
                  inputs.current[field.name] = input
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Not drawn until the answer could be one (OTP – Input Complete,
            1951:8202): it rises from under the keyboard the way the amount
            screen's submit does, and leaves the same way. */}
        <Box style={styles.action}>
          {!isSubmitting &&
          (shownError !== undefined || (attempted && !isComplete)) ? (
            <FiatErrorNotice
              message={
                shownError ??
                incompleteMessage(t, fields, submittedValues, defaultRegion)
              }
            />
          ) : null}
          {isSubmitting ? <FiatSubmissionIndicator /> : null}
          {showContinue && !isSubmitting && !rejected ? (
            <FiatActionButton label={t('common.continue')} onPress={submit} />
          ) : null}
        </Box>
      </ScrollView>

      {/* Always mounted: gorhom presents and dismisses it through the handle
          the hook holds, and the rows stay drawn while it animates out. */}
      <FiatCountrySheet sheet={countrySheet.sheet} {...picking} />
    </Animated.View>
  )
}

/** Nothing to choose for, so the choice goes nowhere. */
const noop = () => undefined

/** The chip each phone field opens on where a number is already known. */
function knownPhoneCountries(
  fields: readonly SessionContactField[],
  phone: string | undefined,
): Record<string, string> {
  const parsed = phone === undefined ? undefined : pastedPhone(phone)
  if (parsed === undefined) {
    return {}
  }
  return Object.fromEntries(
    fields
      .filter((field) => field.kind === 'PHONE')
      .map((field) => [field.name, parsed.country.alpha2]),
  )
}

/** Normalises a number arriving whole, and records the country it moved to. */
function insertedPhoneEdit(
  draft: string,
  next: string,
  chip: PhoneCountry,
): DialledPhone | undefined {
  const inserted = insertedPhone(draft, next, chip)
  if (inserted !== undefined && inserted.country.alpha2 !== chip.alpha2) {
    logger.info('connect-rn:fiat:phone_autofill_normalized', {
      from: chip.alpha2,
      to: inserted.country.alpha2,
    })
  }
  return inserted
}

/**
 * One field, worded for the channel it collects — one literal key per branch,
 * as the i18n rule requires.
 */
function ContactField({
  field,
  value,
  country,
  isPickingCountry,
  autoFocus,
  editable,
  invalid,
  isLast,
  onChangeText,
  onOpenCountries,
  onAdvance,
  onFocus,
  onLayoutRef,
}: {
  field: SessionContactField
  value: string
  country: PhoneCountry
  isPickingCountry: boolean
  autoFocus: boolean
  editable: boolean
  invalid: boolean
  isLast: boolean
  onChangeText: (next: string) => void
  onOpenCountries: () => void
  onAdvance: () => void
  onFocus: () => void
  onLayoutRef: (input: RNTextInput | null) => void
}) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()

  // A number is never typed whole: the rail takes E.164 only, so the code is
  // chosen and the box holds the rest.
  if (field.kind === 'PHONE') {
    return (
      <FiatPhoneField
        country={country}
        national={value}
        isOpen={isPickingCountry}
        autoFocus={autoFocus}
        editable={editable}
        returnKeyType={returnKeyFor('number-pad', isLast)}
        onChangeNational={onChangeText}
        onOpenCountries={onOpenCountries}
        onSubmitEditing={onAdvance}
        onFocus={onFocus}
        onLayoutRef={onLayoutRef}
      />
    )
  }

  return (
    // No focus ring: the design draws these plain, and the placeholder is
    // what tells one from the other.
    <Box
      style={[
        styles.field,
        {
          backgroundColor: invalid
            ? theme.form.fieldInvalid.fill
            : theme.form.field,
        },
      ]}
    >
      <TextInput
        ref={onLayoutRef}
        value={value}
        onChangeText={onChangeText}
        placeholder={t('fiat.emailPlaceholder')}
        placeholderTextColor={theme.content.elements.tertiary}
        keyboardType="email-address"
        textContentType="emailAddress"
        autoComplete="email"
        autoCapitalize="none"
        autoCorrect={false}
        spellCheck={false}
        autoFocus={autoFocus}
        editable={editable}
        returnKeyType="next"
        submitBehavior="submit"
        onSubmitEditing={onAdvance}
        onFocus={onFocus}
        style={[
          styles.input,
          inputTextStyle(theme),
          { color: theme.content.elements.primary },
        ]}
      />
    </Box>
  )
}

/** `next` on a field that hands focus on, `done` on the one that submits. */
function returnKeyFor(
  keyboardType: 'number-pad' | 'email-address',
  isLast: boolean,
): 'next' | 'done' | undefined {
  if (PADS_WITHOUT_RETURN_KEY.has(keyboardType)) {
    return undefined
  }
  return isLast ? 'done' : 'next'
}

/** Names the channel still wanting an answer, not whichever field came last. */
function incompleteMessage(
  t: ReturnType<typeof useFunkitTranslation>['t'],
  fields: readonly SessionContactField[],
  values: Record<string, string>,
  region?: string,
): string {
  const incomplete = fields.filter(
    (field) => !isCompleteContact(field, values[field.name] ?? '', region),
  )
  const kinds = new Set(incomplete.map((field) => field.kind))
  if (kinds.size > 1) {
    return t('fiat.contactVerifyBoth')
  }
  if (kinds.has('PHONE')) {
    return t('fiat.contactVerifyPhone')
  }
  return t('fiat.contactInvalidEmail')
}

/**
 * The channel variant, as the design words it. A step wanting both says so
 * once, rather than wording itself for whichever field happens to be first.
 */
function heading(
  t: ReturnType<typeof useFunkitTranslation>['t'],
  fields: readonly SessionContactField[],
): string {
  const kinds = new Set(fields.map((field) => field.kind))
  if (kinds.size > 1) {
    return t('fiat.contactVerifyBoth')
  }
  return kinds.has('PHONE')
    ? t('fiat.contactVerifyPhone')
    : t('fiat.contactVerifyEmail')
}

/**
 * Read off 1950:3326 (393x852). This step draws one heading where the KYC
 * form draws a title over a paragraph, so it sets its own rather than
 * borrowing the form's.
 */
const CONTACT_METRICS = {
  /** Disc bottom (108) to the heading's line box (160). */
  headingTop: 52,
  /** The heading is inset past the fields, not aligned to them. */
  headingInset: 6,
  /** Heading box (208) to the first field (235). */
  headingBottom: 27,
  headingTracking: -0.42,
  headingWidth: 219,
  headingHeight: 48,
  headingLineHeight: 23.52,
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  content: {
    flexGrow: 1,
    paddingTop: CONTACT_METRICS.headingTop,
    paddingHorizontal: KYC_FORM_METRICS.screenInset,
    gap: KYC_FORM_METRICS.advanceGap,
  },
  // The heading and the fields it labels; the container's own gap is what
  // holds the action clear of them.
  ask: { gap: CONTACT_METRICS.headingBottom },
  heading: {
    paddingHorizontal: CONTACT_METRICS.headingInset,
    fontSize: KYC_FORM_METRICS.detailFontSize,
    lineHeight: CONTACT_METRICS.headingLineHeight,
    maxWidth: CONTACT_METRICS.headingWidth + 2 * CONTACT_METRICS.headingInset,
    minHeight: CONTACT_METRICS.headingHeight,
    letterSpacing: CONTACT_METRICS.headingTracking,
  },
  // The same gap the KYC form sets between the rows of one section.
  rows: { gap: KYC_FORM_METRICS.rowGap },
  error: { textAlign: 'center' },
  field: {
    height: KYC_FORM_METRICS.fieldHeight,
    borderRadius: KYC_FORM_METRICS.fieldRadius,
    paddingHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal,
    justifyContent: 'center',
  },
  // Undo the platform's own padding so the field's height is the box's.
  input: { padding: 0 },
  // The design rests the action at the foot, riding up on the keyboard. The
  // slot holds its height whether or not the action is drawn, so the fields
  // above it stay put as it arrives.
  action: { marginTop: 'auto', minHeight: KYC_FORM_METRICS.fieldHeight },
})
